import { useState, useRef, useEffect } from "react";
import { ChevronDown, MapPin, X } from "lucide-react";
import { searchCities, getProvinceByCity, getCapByCity } from "../data/italianCities";

/**
 * Componente autocomplete per città italiane
 * Quando si seleziona una città, compila automaticamente provincia e CAP
 */
export default function CityAutocomplete({
  value,
  onChange,
  onProvinceChange,
  onCapChange,
  placeholder = "Cerca città...",
  className = "",
  disabled = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value || "");
  const [results, setResults] = useState([]);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Sincronizza searchTerm con value esterno
  useEffect(() => {
    setSearchTerm(value || "");
  }, [value]);

  // Chiudi dropdown quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Cerca città mentre si digita
  const handleInputChange = (e) => {
    const query = e.target.value;
    setSearchTerm(query);
    onChange(query);
    
    if (query.length >= 2) {
      const matches = searchCities(query);
      setResults(matches);
      setIsOpen(matches.length > 0);
    } else {
      setResults([]);
      setIsOpen(false);
    }
  };

  // Seleziona una città dalla lista
  const handleSelect = (city) => {
    setSearchTerm(city.city);
    onChange(city.city);
    
    // Auto-compila provincia
    if (onProvinceChange) {
      onProvinceChange(city.province);
    }
    
    // Auto-compila CAP
    if (onCapChange) {
      onCapChange(city.cap);
    }
    
    setIsOpen(false);
    setResults([]);
  };

  // Pulisci input
  const handleClear = () => {
    setSearchTerm("");
    onChange("");
    if (onProvinceChange) onProvinceChange("");
    if (onCapChange) onCapChange("");
    setResults([]);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => {
            if (searchTerm.length >= 2) {
              const matches = searchCities(searchTerm);
              setResults(matches);
              setIsOpen(matches.length > 0);
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full pl-10 pr-16 h-10 bg-zinc-900 border border-zinc-700 rounded-lg text-white placeholder:text-zinc-500 focus:border-primary focus:ring-1 focus:ring-primary outline-none disabled:opacity-50"
          data-testid="city-autocomplete-input"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {searchTerm && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 text-zinc-500 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </div>
      </div>

      {/* Dropdown risultati */}
      {isOpen && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-zinc-900 border border-zinc-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {results.map((city, index) => (
            <button
              key={`${city.city}-${city.province}-${index}`}
              type="button"
              onClick={() => handleSelect(city)}
              className="w-full px-4 py-2.5 text-left hover:bg-zinc-800 flex items-center justify-between group transition-colors"
            >
              <span className="text-white group-hover:text-primary">{city.city}</span>
              <span className="text-zinc-500 text-sm">
                {city.province} • {city.cap}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
